/**
 * Money formatting for the payment pages (pricing, coin top-up, coin balance) for the INR-only
 * currency behaviour (SCRUM-365, ADR-164). Pure helpers, no React.
 *
 * The currency is always the one the backend returned; nothing here falls back to a literal
 * currency. Symbols come from `currencySymbol` (src/lib/currency.ts), never from `Intl`.
 */
import { currencySymbol } from '@/lib/currency';
import type { CoinPackage, PurchaseCurrency } from '@/services/coinPurchaseService';

function formatMajor(major: number): string {
  return Number.isInteger(major) ? major.toLocaleString() : major.toFixed(2);
}

/**
 * Plan/tier price from minor units, e.g. formatPlanMinor(199900, 'INR') -> "₹1,999".
 * A missing currency gives the amount with NO symbol (never "—", never a literal).
 */
export function formatPlanMinor(minor: number, currency: string | null | undefined): string {
  return `${currencySymbol(currency)}${formatMajor(minor / 100)}`;
}

/**
 * Coin package amount (minor units) for the backend currency: INR -> paise, USD -> cents.
 * Any other or missing currency -> null (the pack column cannot be guessed).
 */
export function packAmountMinorFor(
  pkg: CoinPackage,
  currency: string | null | undefined
): number | null {
  if (currency === 'INR') return pkg.amountInrPaise;
  if (currency === 'USD') return pkg.amountUsdCents;
  return null;
}

/** Coin pack price: null amount -> "—", else formatted like a plan amount. */
export function formatPackMinor(minor: number | null, currency: string | null | undefined): string {
  if (minor === null) return '—';
  return formatPlanMinor(minor, currency);
}

/** The purchase-order currency for a backend currency: INR/USD pass through, else null. */
export function toPurchaseCurrency(currency: string | null | undefined): PurchaseCurrency | null {
  if (currency === 'INR' || currency === 'USD') return currency;
  return null;
}
